import { useEffect } from "react";
import { ArrowRight, ArrowUpRight, Check } from "lucide-react";
import { Link } from "@/components/LLink";
import { Reveal } from "@/components/motion";
import ContactForm from "@/components/ContactForm";
import SEO from "@/components/SEO";
import { useLang } from "@/i18n";
import { SERVICES_HUB } from "@/seo/servicesHubContent";

const HERO = "/images/servicii-hero.jpg";
const BG = "/images/sudura.jpg";
const H2 = "mt-5 font-heading text-3xl font-semibold tracking-tight md:text-[40px]";
const WIDE = "px-4 py-24 sm:px-6 lg:px-10 2xl:px-16";
const scrollTo = (id) => document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });

function useSchema(c) {
  useEffect(() => {
    const el = document.createElement("script");
    el.type = "application/ld+json";
    el.text = JSON.stringify([
      { "@context": "https://schema.org", "@type": "FAQPage", mainEntity: c.faq.map(([q, a]) => ({ "@type": "Question", name: q, acceptedAnswer: { "@type": "Answer", text: a } })) },
      { "@context": "https://schema.org", "@type": "ItemList", itemListElement: c.services.map((s, i) => ({ "@type": "ListItem", position: i + 1, name: s.t, url: `${window.location.origin}/servicii/${s.slug}` })) },
    ]);
    document.head.appendChild(el);
    return () => el.remove();
  }, [c]);
}

export default function ServicesPage() {
  const { lang } = useLang();
  const c = SERVICES_HUB[lang] || SERVICES_HUB.ro;
  useSchema(c);

  return (
    <div data-testid="services-page">
      <SEO metaKey="servicii" title={c.seo[0]} description={c.seo[1]} image={HERO} />

      <section className="relative overflow-hidden bg-[#0b1220] text-white" data-testid="services-hero">
        <div className="grid lg:grid-cols-12 lg:min-h-[660px]">
          <div className="relative z-10 flex flex-col justify-end px-4 pb-16 pt-36 sm:px-6 lg:col-span-6 lg:px-10 lg:pb-20 2xl:px-16">
            <p className="text-[11px] font-semibold uppercase tracking-[0.3em] text-white/60">{c.kicker}</p>
            <h1 className="mt-6 max-w-2xl font-heading text-4xl font-semibold leading-[1.02] tracking-tight sm:text-5xl lg:text-6xl" data-testid="services-title">{c.title}</h1>
            <p className="mt-6 max-w-xl text-base leading-relaxed text-white/70 md:text-lg">{c.sub}</p>
            <div className="mt-8 flex flex-wrap gap-3">
              <button onClick={() => scrollTo("services-form")} data-testid="services-hero-cta" className="inline-flex items-center gap-2 bg-white px-6 py-3.5 text-[13px] font-semibold text-ink transition-colors hover:bg-gold">{c.cta_k} <ArrowRight className="h-4 w-4" /></button>
              <button onClick={() => scrollTo("services-why")} data-testid="services-hero-why" className="inline-flex items-center gap-2 border border-white/30 px-6 py-3.5 text-[13px] font-semibold text-white transition-colors hover:border-white">{c.why_k} <ArrowUpRight className="h-4 w-4" /></button>
            </div>
            <ul className="mt-10 flex flex-wrap gap-x-8 gap-y-3 border-t border-white/15 pt-6" data-testid="services-badges">
              {c.badges.map((b) => <li key={b} className="flex items-center gap-2 text-[13px] font-semibold text-white/85"><Check className="h-4 w-4 text-gold" /> {b}</li>)}
            </ul>
          </div>
          <div className="relative min-h-[320px] lg:col-span-6">
            <img src={HERO} alt={c.title} className="absolute inset-0 h-full w-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-r from-[#0b1220] via-[#0b1220]/30 to-transparent lg:via-transparent" />
            <div className="absolute inset-0 bg-gradient-to-t from-[#0b1220]/70 to-transparent lg:hidden" />
          </div>
        </div>
      </section>

      <section className="border-b border-ink/10 bg-surface" data-testid="services-list">
        <div className={WIDE}>
          <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
            <div><p className="kicker">{c.srv_k}</p><h2 className={H2}>{c.srv_t}</h2></div>
            <p className="max-w-md text-[15px] text-ink/65 lg:text-right">{c.srv_p}</p>
          </div>
          <div className="mt-12 grid gap-4 md:grid-cols-2 xl:grid-cols-4">
            {c.services.map((s, i) => (
              <Reveal key={s.slug} delay={i * 0.08}>
                <article className="group flex h-full flex-col border border-ink/10 bg-base transition-shadow duration-500 hover:shadow-2xl hover:shadow-black/10" data-testid={`service-card-${s.slug}`}>
                  <Link to={`/servicii/${s.slug}`} className="relative block h-56 overflow-hidden bg-[#0b1220]" data-testid={`service-card-img-${s.slug}`}>
                    <img src={s.img} alt={s.t} loading="lazy" className="h-full w-full object-cover opacity-90 transition-transform duration-700 group-hover:scale-[1.05]" />
                    <span className="absolute left-5 top-5 font-mono text-[11px] tracking-[0.25em] text-white/80">0{i + 1}</span>
                  </Link>
                  <div className="flex flex-1 flex-col p-6">
                    <h3 className="font-heading text-xl font-semibold text-ink">{s.t}</h3>
                    <p className="mt-3 text-[14px] leading-relaxed text-ink/65">{s.x}</p>
                    <ul className="mt-5 space-y-2">{s.items.map((it) => <li key={it} className="flex items-start gap-2 text-[13px] text-ink/80"><span className="mt-2 h-1 w-1 shrink-0 bg-gold" aria-hidden /> {it}</li>)}</ul>
                    <Link to={`/servicii/${s.slug}`} data-testid={`service-card-link-${s.slug}`} className="mt-auto inline-flex items-center gap-2 pt-6 text-[13px] font-semibold text-ink underline decoration-gold decoration-2 underline-offset-[6px] transition-colors hover:text-gold">{c.detail_k} <ArrowRight className="h-3.5 w-3.5" /></Link>
                  </div>
                </article>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section id="services-why" className="relative scroll-mt-24 overflow-hidden border-b border-ink/10 bg-[#0b1220] text-white" data-testid="services-why">
        <img src={BG} alt="" aria-hidden className="pointer-events-none absolute inset-0 h-full w-full object-cover opacity-[0.14]" />
        <div className={`${WIDE} relative grid gap-12 lg:grid-cols-12`}>
          <div className="lg:col-span-4">
            <p className="kicker text-white/60">{c.why_k}</p>
            <h2 className={`${H2} text-white`}>{c.why_t}</h2>
            <p className="mt-6 text-[15px] leading-[1.7] text-white/65">{c.why_p}</p>
            <button onClick={() => scrollTo("services-form")} data-testid="services-why-cta" className="mt-10 inline-flex items-center gap-2 bg-white px-6 py-3.5 text-[13px] font-semibold text-ink transition-colors hover:bg-gold">{c.cta_k} <ArrowRight className="h-4 w-4" /></button>
          </div>
          <ol className="grid gap-px bg-white/10 sm:grid-cols-2 lg:col-span-8 lg:grid-cols-3">
            {c.why.map(([t, x], i) => <li key={t} className="group bg-[#0b1220]/90 p-8 backdrop-blur-sm"><span className="font-mono text-[12px] tracking-[0.25em] text-white/40 transition-colors group-hover:text-gold">0{i + 1}</span><h3 className="mt-6 text-[17px] font-bold">{t}</h3><p className="mt-3 text-[15px] leading-[1.6] text-white/65">{x}</p></li>)}
          </ol>
        </div>
      </section>

      <section className="border-b border-ink/10" data-testid="services-table">
        <div className={WIDE}>
          <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
            <div><p className="kicker">{c.tab_k}</p><h2 className={H2}>{c.tab_t}</h2></div>
            <p className="max-w-lg text-[15px] text-ink/65 lg:text-right">{c.tab_p}</p>
          </div>
          <div className="mt-12 overflow-x-auto border border-ink/10">
            <table className="w-full min-w-[860px] text-left" data-testid="services-table-el">
              <thead><tr className="bg-[#0b1220] text-white">{c.tab_head.map((h) => <th key={h} className="px-6 py-4 text-[11px] font-semibold uppercase tracking-[0.2em]">{h}</th>)}</tr></thead>
              <tbody>{c.rows.map((row, i) => <tr key={row[0]} className={`border-t border-ink/10 ${i % 2 ? "bg-surface" : "bg-base"}`}>{row.map((cell, j) => <td key={j} className={`px-6 py-5 text-[15px] ${j === 0 ? "font-semibold text-ink" : j === 4 ? "text-ink/65" : "text-ink/75"}`}>{cell}</td>)}</tr>)}</tbody>
            </table>
          </div>
        </div>
      </section>

      <section className="border-b border-ink/10 bg-surface" data-testid="services-flow">
        <div className={WIDE}>
          <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
            <div><p className="kicker">{c.flow_k}</p><h2 className={H2}>{c.flow_t}</h2></div>
            <p className="max-w-md text-[15px] text-ink/65 lg:text-right">{c.flow_p}</p>
          </div>
          <div className="mt-12 grid gap-px border border-ink/10 bg-ink/10 sm:grid-cols-2 lg:grid-cols-4">
            {c.flow.map(([t, x], i) => (
              <Link key={t} to={`/servicii/${c.services[i].slug}`} data-testid={`services-flow-${i}`} className="group bg-base p-8 transition-colors hover:bg-surface lg:p-10">
                <div className="flex items-start justify-between"><span className="font-heading text-5xl font-semibold text-ink/10 transition-colors duration-500 group-hover:text-gold">0{i + 1}</span><ArrowUpRight className="h-5 w-5 text-ink/30 transition-colors group-hover:text-gold" /></div>
                <h3 className="mt-8 text-[17px] font-bold text-ink">{t}</h3>
                <p className="mt-3 text-[15px] leading-[1.6] text-ink/65">{x}</p>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <section className="border-b border-ink/10" data-testid="services-faq">
        <div className={`${WIDE} grid gap-12 lg:grid-cols-12`}>
          <div className="lg:col-span-4"><p className="kicker">{c.faq_k}</p><h2 className={H2}>{c.faq_t}</h2></div>
          <div className="border-t border-ink/10 lg:col-span-8">
            {c.faq.map(([q, a], i) => <details key={q} className="group border-b border-ink/10" data-testid={`services-faq-${i}`}><summary className="flex cursor-pointer list-none items-center justify-between gap-6 py-5 text-[17px] font-semibold text-ink [&::-webkit-details-marker]:hidden">{q}<span className="shrink-0 font-mono text-xl text-ink/40 transition-transform duration-300 group-open:rotate-45">+</span></summary><p className="pb-6 text-[15px] leading-[1.7] text-ink/65">{a}</p></details>)}
          </div>
        </div>
      </section>

      <section id="services-form" className="scroll-mt-24">
        <div className={`${WIDE} grid gap-16 lg:grid-cols-2`}>
          <div><p className="kicker">{c.cta_k}</p><h2 className={`${H2} leading-[1.1]`}>{c.cta_t}</h2><p className="mt-6 max-w-md text-[15px] leading-[1.7] text-ink/65">{c.cta_p}</p></div>
          <Reveal delay={0.1}><ContactForm defaultSubject={c.form_subject} /></Reveal>
        </div>
      </section>
    </div>
  );
}
